import { EstateCardProp } from "@/types";
import { formatCzDate } from "@/lib/date";
import { formatCzkPrice } from "@/lib/price";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import Link from "next/link";
import { useLanguage } from "@/context/LanguageContext";
import { getEstateValueLabel } from "@/utils/estateValueLabels";
import { EstateOfferImage } from "@/components/hlidaci-pes/EstateOfferImage";
import { SoftiqIndexDialog } from "@/components/hlidaci-pes/SoftiqIndexDialog";

export function EstateCard({ estateOffer, isMainPage }: EstateCardProp) {
  const { language } = useLanguage();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isIndexDialogOpen, setIsIndexDialogOpen] = useState(false);
  const translateClass = isDetailsOpen ? "translate-y-0" : "translate-y-[75%]";
  const offerTypeLabel = getEstateValueLabel(
    estateOffer.watchdogEntity.offerType,
    language,
  );
  const estateTypeLabel = getEstateValueLabel(
    estateOffer.watchdogEntity.estateType,
    language,
  );
  const searchAddressLabel = getEstateValueLabel(
    estateOffer.watchdogEntity.searchAddress,
    language,
  );

  return (
    <div
      className={`relative w-full overflow-hidden rounded-lg shadow-lg ${isMainPage ? "h-full" : "h-[250px]"}`}
    >
      <Link
        href={estateOffer.offerUrl}
        target="_blank"
        className="block w-full h-full relative "
      >
        <EstateOfferImage
          source={estateOffer.mainPicUrl}
          origin={estateOffer.origin}
          offerUrl={estateOffer.offerUrl}
          alt={estateOffer.header}
          sizes="(min-width: 1024px) 50vw, 100vw"
        />
      </Link>

      <div
        className={`absolute -bottom-0 w-full transform transition-transform ${
          isDetailsOpen ? "translate-y-0" : "translate-y-[calc(100%-0.5rem)]"
        }`}
      >
        {/* Notch button - moves with the panel */}
        <div className="absolute -top-[1.063rem] left-1/2 -translate-x-1/2 z-20 cursor-pointer" onClick={(e) => {
          e.stopPropagation();
          setIsDetailsOpen(!isDetailsOpen);
        }}>
          <div className="bg-white/80 backdrop-blur-sm rounded-t-lg px-1.5 py-0.5">
            {isDetailsOpen ? (
              <ChevronDownIcon className="h-3.5 w-3.5 text-softiq-primary" />
            ) : (
              <ChevronUpIcon className="h-3.5 w-3.5 text-softiq-primary" />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white/80 backdrop-blur-sm rounded-t-2xl overflow-hidden hover:backdrop-blur-md text-softiq-secondary cursor-pointer" onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
          <div className="px-3 pb-2 pt-2">

          {/*Grid layout with offer details*/}
          <div className="grid grid-cols-2 gap-1 pb-1.5">
            {/* TITLE + ADDRESS */}
            <div className="col-span-2 rounded-lg bg-[#f4f7f5] px-1.5 py-1">
              <span className="block text-[0.55rem] text-[#7DA596] whitespace-nowrap">
                Nabídka
              </span>
              <span className="block text-[0.75rem] font-semibold text-[#1B4242] truncate leading-tight">
                {estateOffer.header
                  ? estateOffer.header.charAt(0).toUpperCase() +
                    estateOffer.header.slice(1)
                  : ""}
              </span>
              <span className="block text-[0.65rem] text-[#1B4242] truncate leading-tight">
                {estateOffer.address}
              </span>
            </div>

            {/* TOTAL PRICE */}
            <div className="rounded-lg bg-[#f4f7f5] px-1.5 py-1">
              <span className="block text-[0.55rem] text-[#7DA596] whitespace-nowrap">
                Cena
              </span>
              <span className="block text-[0.85rem] font-bold text-[#1B4242] whitespace-nowrap leading-tight">
                {formatCzkPrice(estateOffer.price)}
              </span>
            </div>

            {/* PRICE / M² */}
            <div className="rounded-lg bg-[#f4f7f5] px-1.5 py-1">
              <span className="block text-[0.55rem] text-[#7DA596] whitespace-nowrap">
                Cena za m²
              </span>
              <span className="block text-[0.75rem] font-semibold text-[#1B4242] whitespace-nowrap leading-tight">
                {formatCzkPrice(estateOffer.pricePerSquareMeter)}
              </span>
            </div>

            {/* AREA */}
            <div className="rounded-lg bg-[#f4f7f5] px-1.5 py-1">
              <span className="block text-[0.55rem] text-[#7DA596] whitespace-nowrap">
                Plocha
              </span>
              <span className="block text-[0.75rem] font-semibold text-[#1B4242] whitespace-nowrap leading-tight">
                {estateOffer.squareMeter} m²
              </span>
            </div>

            {/* SOFTIQ INDEX */}
            {estateOffer.softiqIndex != null && (
              <div
                className="rounded-lg bg-[#f4f7f5] px-1.5 py-1 flex flex-col items-center justify-center cursor-pointer hover:bg-[#e8ede9] transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsIndexDialogOpen(true);
                }}
              >
                <div className="text-[0.48rem] font-semibold tracking-[1.5px] uppercase text-[#7DA596]">
                  SOFTIQ INDEX
                </div>

                <span
                  className="block text-[0.8rem] font-bold leading-none my-0.5"
                  style={{
                    color:
                      Number(estateOffer.softiqIndex) >= 1.5
                        ? "#4F8067"
                        : Number(estateOffer.softiqIndex) >= -1.5
                          ? "#697875"
                          : "#B64F4F",
                  }}
                >
                  {Math.round(Number(estateOffer.softiqIndex))}
                </span>

                <div className="relative w-[55px]">
                  {/* Scale */}
                  <div
                    className="relative h-[2.5px] w-full rounded-full"
                    style={{
                      background:
                        "linear-gradient(to right, #C65B5B 0%, #E3A17E 25%, #D7DEDA 50%, #8BB49F 75%, #4F8067 100%)",
                    }}
                  >
                    {/* Zero marker */}
                    <div className="absolute left-1/2 -top-px h-[4px] w-px bg-white" />

                    {/* Index marker */}
                    <div
                      className="absolute -top-0.5 h-1.5 w-1.5 -translate-x-1/2 rounded-full border bg-white shadow-[0_1px_3px_rgba(27,66,66,0.15)]"
                      style={{
                        left: `${Math.max(0, Math.min(100, ((Number(estateOffer.softiqIndex) + 10) / 20) * 100))}%`,
                        borderColor:
                          Number(estateOffer.softiqIndex) >= 1.5
                            ? "#4F8067"
                            : Number(estateOffer.softiqIndex) >= -1.5
                              ? "#697875"
                              : "#C65B5B",
                      }}
                    />
                  </div>

                  {/* Scale labels */}
                  <div className="mt-0.5 flex w-full justify-between text-[0.4rem] leading-none text-[#879590]">
                    <span>-10</span>
                    <span>0</span>
                    <span>+10</span>
                  </div>
                </div>
              </div>
            )}

            {/* DATE */}
            <span className="text-[0.55rem] font-medium text-black whitespace-nowrap px-1.5 py-1 flex items-center">
              {formatCzDate(estateOffer.createdAt)}
            </span>
          </div>
          </div>
        </div>
      </div>

      <SoftiqIndexDialog
        isOpen={isIndexDialogOpen}
        onClose={() => setIsIndexDialogOpen(false)}
      />
    </div>
  );
}
